import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppContext } from '../../data/app-context';
import { PowerShellCommand } from '../../data/powershell';
import { EnvironmentModuleToolState } from '../../manifest/environment-modules';
import { SharedCache, SharedCacheOptions } from '../shared-cache';
import { ToolInventory, ToolInventoryData, ToolInventoryParams, ToolInventoryProperty } from './tool-inventory';

/**
 * Tool Inventory cache class.
 */
export class ToolInventoryCache extends SharedCache<ToolInventory, ToolInventoryData, ToolInventoryParams> {
    private static uniqueId = '@msft-sme/shell:toolInventory';
    private static uniqueVersion = 0;

    /**
     * Initializes a new instance of the ToolInventoryCache class.
     *
     * @param appContext the app context.
     * @param options the option of shared cache.
     */
    constructor(private appContext: AppContext, options?: SharedCacheOptions) {
        super(
            ToolInventoryCache.uniqueId,
            ToolInventoryCache.uniqueVersion,
            (params) => this.dataInstanceId(params),
            (instance) => this.dataSerialize(instance),
            (serialized) => this.dataDeserialize(serialized),
            (params) => this.dataQuery(params),
            options);
    }

    /**
     * Defines how to collect the tool inventory data.
     *
     * @param params the server inventory query params.
     * @return the Observable of ServerInventory data.
     */
    private dataQuery(params: ToolInventoryParams): Observable<ToolInventory> {
        const psSession = this.appContext.powerShell.createSession(params.name, null, params.requestOptions);
        return this.appContext.powerShell.run(
            psSession,
            params.command ? <PowerShellCommand>{ command: params.command, script: params.script, module: params.module } : params.script)
            .pipe(
                map((data: any) => {
                    const inventory = new ToolInventory(params.name);
                    if (data && data.results && data.results.length > 0) {
                        const result = data.results[0];
                        inventory.name = params.name;
                        inventory.id = params.id;
                        switch (result.state) {
                            case EnvironmentModuleToolState[EnvironmentModuleToolState.Available]:
                                inventory.state = EnvironmentModuleToolState.Available;
                                break;
                            case EnvironmentModuleToolState[EnvironmentModuleToolState.NotConfigured]:
                                inventory.state = EnvironmentModuleToolState.NotConfigured;
                                break;
                            case EnvironmentModuleToolState[EnvironmentModuleToolState.NotSupported]:
                            default:
                                inventory.state = EnvironmentModuleToolState.NotSupported;
                                break;
                        }

                        inventory.message = result.message;
                        inventory.properties = [];
                        const properties: ToolInventoryProperty[] = result.properties;
                        if (properties) {
                            for (const pair of properties) {
                                inventory.properties.push(pair);
                            }
                        }
                    }

                    return inventory;
                }));
    }

    /**
     * Defines how to identify the cache entry by params.
     *
     * @param params the tool inventory query params.
     * @return the id string.
     */
    private dataInstanceId(params: ToolInventoryParams): string {
        return `${params.name}:${params.id}`;
    }

    /**
     * Defines how to deserialize the class object from seralized data.
     *
     * @param serialized the serialized string;
     */
    private dataDeserialize(serialized: string): ToolInventory {
        const inventory: ToolInventoryData = JSON.parse(serialized);
        return new ToolInventory(inventory.name, inventory);
    }

    /**
     * Defines how to serialize the class object to seralized data.
     *
     * @param instance the class instance.
     */
    private dataSerialize(instance: ToolInventory): string {
        // automatically stripped out class related data.
        return JSON.stringify(instance);
    }
}
